import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import {
  Trash2, Minus, Plus, CheckCircle, ChevronLeft,
  Mail, AlertCircle, Tag, Download, Smartphone, Package
} from 'lucide-react';
import { useCart } from '@/hooks/useCart';
import { orderApi } from '@/api-client';
import { getPriceStr } from '@/types/product';

interface ShippingAddress {
  name: string;
  street: string;
  zip: string;
  city: string;
}

const PAYPAL_CLIENT_ID = import.meta.env.VITE_PAYPAL_CLIENT_ID || '';

// ═══ ROBUSTE Android-Erkennung ═══
const isAndroid = () => {
  const ua = navigator.userAgent.toLowerCase();
  const platform = (navigator as any).userAgentData?.platform?.toLowerCase?.() || '';
  
  const isAndroidDevice = /android/.test(ua) || platform === 'android';
  const isIOS = /iphone|ipad|ipod/.test(ua) || platform === 'ios';
  
  return isAndroidDevice && !isIOS;
};

// ═══ Deep Link für Installer App ═══
const getInstallerIntent = (token: string, slug: string) => {
  const downloadUrl = encodeURIComponent(`https://jls-software.de/api/download.php?token=${token}&app=${slug}`);
  const fallback = encodeURIComponent('https://jls-software.de/downloads/jl-installer.apk');
  return `intent://install?token=${token}&app=${slug}&url=${downloadUrl}#Intent;scheme=jls-software;package=com.jlsoftware.installer;S.browser_fallback_url=${fallback};end`;
};

export function Checkout() {
  const {
    cart, totalPrice, rawTotal, discount, voucher,
    applyVoucher, removeVoucher, needsShipping,
    updateQuantity, removeItem, clearCart,
  } = useCart();

  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState('');
  const [emailConfirm, setEmailConfirm] = useState('');
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [downloadTokens, setDownloadTokens] = useState<{ token: string; name: string; slug: string }[]>([]);
  const [orderId, setOrderId] = useState('');
  const [isFreeOrder, setIsFreeOrder] = useState(false);

  const [shipping, setShipping] = useState<ShippingAddress>({
    name: '', street: '', zip: '', city: '',
  });

  const shippingCost = needsShipping && rawTotal < 50 ? 3.99 : 0;
  const grandTotal = totalPrice + shippingCost;

  // ═══ DEBUG: Zeige User-Agent in Konsole ═══
  useEffect(() => {
    console.log('[Checkout] User-Agent:', navigator.userAgent);
    console.log('[Checkout] isAndroid():', isAndroid());
    console.log('[Checkout] Platform:', (navigator as any).userAgentData?.platform || 'unknown');
  }, []);

  const validateEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
  const validateZip = (val: string) => /^\d{5}$/.test(val.trim());

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!email || !validateEmail(email)) errors.email = 'Bitte gib eine gültige E-Mail-Adresse ein.';
    if (email !== emailConfirm) errors.emailConfirm = 'Die E-Mail-Adressen stimmen nicht überein.';
    if (!agbAccepted) errors.agb = 'Bitte akzeptiere die AGB und Datenschutzerklärung.';
    if (needsShipping) {
      if (!shipping.name.trim()) errors.shippingName = 'Name ist erforderlich.';
      if (!shipping.street.trim()) errors.shippingStreet = 'Adresse ist erforderlich.';
      if (!validateZip(shipping.zip)) errors.shippingZip = 'Bitte eine gültige 5-stellige PLZ eingeben.';
      if (!shipping.city.trim()) errors.shippingCity = 'Ort ist erforderlich.';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveOrder = async (paypalOrderId?: string) => {
    const wasFree = grandTotal <= 0 && cart.length > 0;
    setIsFreeOrder(wasFree);

    const payload: Record<string, unknown> = {
      email,
      items: cart.map(item => ({
        slug: item.product.slug,
        name: item.product.name,
        price: item.product.price,
      })),
      paid: !!paypalOrderId,
      ...(paypalOrderId && { paypalOrderId }),
    };
    if (needsShipping) payload.shipping = shipping;
    if (voucher) payload.voucherCode = voucher.code;

    const result = await orderApi.create(payload);
    const generatedOrderId = `JL-${Date.now().toString(36).toUpperCase()}`;
    setOrderId(result.orderId || generatedOrderId);

    if (result.tokens && result.tokens.length > 0) {
      setDownloadTokens(result.tokens.map((t: any, i: number) => ({
        ...t,
        slug: cart[i]?.product.slug || t.name.toLowerCase().replace(/\s+/g, '-')
      })));
    } else {
      setDownloadTokens(cart.map(item => ({
        token: item.product.slug,
        name: item.product.name,
        slug: item.product.slug
      })));
    }

    if (voucher) {
      try {
        await fetch('/api/voucher.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'redeem', code: voucher.code, email }),
        });
      } catch { /* ignorieren */ }
    }

    setSubmitted(true);
    clearCart();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOrderError('');
    if (grandTotal > 0) {
      setOrderError('Bitte nutze PayPal für kostenpflichtige Bestellungen.');
      return;
    }
    if (!validateForm()) return;
    setOrderLoading(true);
    try { await saveOrder(); }
    catch (e: any) { setOrderError(e.message || 'Bestellung konnte nicht erstellt werden.'); }
    finally { setOrderLoading(false); }
  };

  const handlePayPalCreateOrder = async (): Promise<string> => {
    setOrderError('');
    if (!validateForm()) throw new Error('Bitte fülle alle Pflichtfelder aus.');

    const res = await fetch('/api/paypal-create.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        amount: grandTotal,
        currency: 'EUR',
        description: `Bestellung bei JL-Software (${cart.map(i => i.product.name).join(', ')})`.substring(0, 127),
      }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'PayPal Order konnte nicht erstellt werden.');
    }

    const data = await res.json();
    if (!data.id) throw new Error('Keine Order ID von PayPal erhalten.');
    return data.id;
  };

  const handlePayPalApprove = async (data: { orderID: string }) => {
    setOrderLoading(true);
    setOrderError('');
    try {
      const captureRes = await fetch('/api/paypal-capture.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderID: data.orderID }),
      });

      const captureData = await captureRes.json();
      if (!captureData.success) {
        throw new Error(captureData.error || 'Zahlung konnte nicht abgeschlossen werden.');
      }

      await saveOrder(data.orderID);
    } catch (e: any) {
      setOrderError(e.message || 'Fehler nach Zahlung. Bitte kontaktiere uns.');
    } finally {
      setOrderLoading(false);
    }
  };

  if (cart.length === 0 && !submitted) {
    return (
      <div className="mt-[70px] pt-20 pb-20 text-center max-w-md mx-auto px-6">
        <h1 className="text-2xl font-bold text-text-primary mb-4">Warenkorb ist leer</h1>
        <p className="text-text-secondary mb-6">Füge Produkte hinzu, um eine Bestellung aufzugeben.</p>
        <Link to="/shop" className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-surface-primary font-semibold rounded-xl hover:shadow-gold transition-all">
          Zum Shop
        </Link>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════
  // ERFOLGREICH BEZAHLT / KOSTENLOS
  // ═══════════════════════════════════════════════════════
  if (submitted) {
    return (
      <div className="mt-[70px] pt-16 pb-20">
        <div className="max-w-lg mx-auto px-6 text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200 }}>
            <CheckCircle size={64} className="text-gold mx-auto mb-6" />
          </motion.div>
          <h1 className="text-3xl font-bold text-text-primary mb-4">
            {isFreeOrder ? 'Bereit zum Download!' : 'Zahlung erfolgreich!'}
          </h1>
          {orderId && (
            <p className="text-text-muted text-sm mb-4">Bestellnummer: <span className="font-mono text-gold">{orderId}</span></p>
          )}
          <p className="text-text-secondary mb-8">
            {isFreeOrder
              ? 'Dein Gutschein hat den vollen Betrag abgedeckt.'
              : 'Vielen Dank für deine Zahlung.'}
          </p>

          {downloadTokens.length > 0 && (
            <div className="mt-8 space-y-4">
              <div className="p-4 bg-surface rounded-2xl border border-white/[0.06]">
                <h3 className="text-lg font-bold text-text-primary mb-4 flex items-center gap-2">
                  <Package size={20} className="text-gold" />Deine Apps & Spiele
                </h3>
                <div className="space-y-3">
                  {downloadTokens.map((item) => (
                    <div key={item.token} className="flex flex-col gap-3 p-4 bg-surface-secondary rounded-xl border border-white/[0.06]">
                      <div className="text-left">
                        <p className="font-semibold text-text-primary text-sm">{item.name}</p>
                        <p className="text-xs text-text-muted">Android APK • Direktinstallation</p>
                      </div>

                      {isAndroid() ? (
                        <div className="flex flex-col gap-2">
                          {/* ═══ Installer Deep Link ═══ */}
                          <a href={getInstallerIntent(item.token, item.slug)}
                            className="flex items-center justify-center gap-2 px-5 py-3 bg-gold text-surface-primary font-semibold rounded-xl hover:shadow-gold hover:-translate-y-0.5 transition-all text-sm">
                            <Smartphone size={16} />Automatisch installieren
                          </a>
                          
                          {/* ═══ Sichtbarer Fallback: Installer-APK ═══ */}
                          <p className="text-xs text-text-muted text-center">
                            Installer nicht installiert?{' '}
                            <a href="/downloads/jl-installer.apk" download className="text-gold hover:underline">
                              JL-Installer herunterladen
                            </a>
                          </p>
                          
                          {/* ═══ Notfall: Direkter Spiel-Download ═══ */}
                          <a href={`https://jls-software.de/api/download.php?token=${item.token}&app=${item.slug}`}
                            download={`${item.slug}.apk`}
                            className="flex items-center justify-center gap-2 px-5 py-2 bg-surface border border-white/[0.06] text-text-muted font-medium rounded-xl hover:border-gold/30 hover:text-text-primary transition-all text-xs">
                            <Download size={14} />Spiel-APK direkt laden (manuell)
                          </a>
                        </div>
                      ) : (
                        /* ═══ Desktop / iOS ═══ */
                        <a href={`https://jls-software.de/api/download.php?token=${item.token}&app=${item.slug}`}
                          download={`${item.slug}.apk`}
                          className="flex items-center justify-center gap-2 px-5 py-3 bg-surface border border-white/[0.06] text-text-primary font-semibold rounded-xl hover:border-gold/50 transition-all text-sm">
                          <Download size={16} />APK herunterladen
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/shop" className="px-6 py-3 bg-gold text-surface-primary font-semibold rounded-xl hover:shadow-gold transition-all">Weiter einkaufen</Link>
            <Link to="/" className="px-6 py-3 border border-white/[0.06] text-text-secondary font-semibold rounded-xl hover:border-gold/25 hover:text-text-primary transition-all">Zur Startseite</Link>
          </div>
        </div>
      </div>
    );
  }

  const updateShipping = (field: keyof ShippingAddress, value: string) => {
    setShipping(prev => ({ ...prev, [field]: value }));
    const key = `shipping${field.charAt(0).toUpperCase() + field.slice(1)}`;
    if (fieldErrors[key]) {
      setFieldErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
    }
  };

  return (
    <div className="mt-[70px]">
      <div className="border-b border-white/[0.06] bg-surface-secondary/50">
        <div className="max-w-[1200px] mx-auto px-6 py-4">
          <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-gold transition-colors">
            <ChevronLeft size={16} />Zurück zum Shop
          </Link>
        </div>
      </div>

      <section className="max-w-[1200px] mx-auto px-6 py-10">
        <h1 className="text-3xl font-bold text-text-primary mb-8">Checkout</h1>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              <div className="bg-surface rounded-2xl border border-white/[0.06] p-6">
                <h2 className="text-lg font-bold text-text-primary mb-5 flex items-center gap-2">
                  <Mail size={18} className="text-gold" />Kontaktdaten
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1.5">E-Mail-Adresse *</label>
                    <input required type="email" value={email}
                      onChange={e => { setEmail(e.target.value); if (fieldErrors.email) setFieldErrors(p => { const n={...p}; delete n.email; return n; }); }}
                      className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:bg-gold/5 focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all ${fieldErrors.email ? 'border-crimson' : 'border-white/[0.06]'}`}
                      placeholder="deine@email.de" />
                    {fieldErrors.email && <p className="text-crimson text-xs mt-1.5">{fieldErrors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1.5">E-Mail bestätigen *</label>
                    <input required type="email" value={emailConfirm}
                      onChange={e => { setEmailConfirm(e.target.value); if (fieldErrors.emailConfirm) setFieldErrors(p => { const n={...p}; delete n.emailConfirm; return n; }); }}
                      className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:bg-gold/5 focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all ${fieldErrors.emailConfirm ? 'border-crimson' : 'border-white/[0.06]'}`}
                      placeholder="deine@email.de wiederholen" />
                    {fieldErrors.emailConfirm && <p className="text-crimson text-xs mt-1.5">{fieldErrors.emailConfirm}</p>}
                  </div>
                </div>
              </div>

              {needsShipping && (
                <div className="bg-surface rounded-2xl border border-white/[0.06] p-6">
                  <h2 className="text-lg font-bold text-text-primary mb-5">Versandadresse</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-text-secondary mb-1.5">Name *</label>
                      <input required value={shipping.name}
                        onChange={e => updateShipping('name', e.target.value)}
                        className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all ${fieldErrors.shippingName ? 'border-crimson' : 'border-white/[0.06]'}`}
                        placeholder="Vor- und Nachname" />
                      {fieldErrors.shippingName && <p className="text-crimson text-xs mt-1.5">{fieldErrors.shippingName}</p>}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-text-secondary mb-1.5">Adresse *</label>
                      <input required value={shipping.street}
                        onChange={e => updateShipping('street', e.target.value)}
                        className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all ${fieldErrors.shippingStreet ? 'border-crimson' : 'border-white/[0.06]'}`}
                        placeholder="Straße & Hausnummer" />
                      {fieldErrors.shippingStreet && <p className="text-crimson text-xs mt-1.5">{fieldErrors.shippingStreet}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-1.5">PLZ *</label>
                      <input required inputMode="numeric" maxLength={5} value={shipping.zip}
                        onChange={e => updateShipping('zip', e.target.value.replace(/\D/g, ''))}
                        className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all ${fieldErrors.shippingZip ? 'border-crimson' : 'border-white/[0.06]'}`}
                        placeholder="12345" />
                      {fieldErrors.shippingZip && <p className="text-crimson text-xs mt-1.5">{fieldErrors.shippingZip}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-1.5">Ort *</label>
                      <input required value={shipping.city}
                        onChange={e => updateShipping('city', e.target.value)}
                        className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all ${fieldErrors.shippingCity ? 'border-crimson' : 'border-white/[0.06]'}`}
                        placeholder="Stadt" />
                      {fieldErrors.shippingCity && <p className="text-crimson text-xs mt-1.5">{fieldErrors.shippingCity}</p>}
                    </div>
                  </div>
                </div>
              )}

              {!needsShipping && (
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl flex items-start gap-3">
                  <CheckCircle size={18} className="text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-green-400 text-sm font-medium">Digitale Lieferung</p>
                    <p className="text-text-secondary text-sm">Kein Versand nötig. Nach Abschluss steht der Download sofort bereit.</p>
                  </div>
                </div>
              )}

              <div className={`flex items-start gap-3 p-4 rounded-xl border ${fieldErrors.agb ? 'bg-crimson/5 border-crimson/30' : 'bg-surface border-white/[0.06]'}`}>
                <input type="checkbox" id="agb" checked={agbAccepted}
                  onChange={e => { setAgbAccepted(e.target.checked); if (fieldErrors.agb) setFieldErrors(p => { const n={...p}; delete n.agb; return n; }); }}
                  className="w-5 h-5 mt-0.5 accent-gold rounded cursor-pointer" />
                <label htmlFor="agb" className="text-sm text-text-secondary cursor-pointer">
                  Ich habe die <Link to="/agb" className="text-gold hover:underline">AGB</Link> und die <Link to="/datenschutz" className="text-gold hover:underline">Datenschutzerklärung</Link> gelesen und stimme ihnen zu. *
                </label>
              </div>
              {fieldErrors.agb && <p className="text-crimson text-xs -mt-4 ml-1">{fieldErrors.agb}</p>}

              {orderError && (
                <div className="p-4 bg-crimson-dim border border-crimson/20 rounded-xl flex items-start gap-3">
                  <AlertCircle size={18} className="text-crimson flex-shrink-0 mt-0.5" />
                  <p className="text-crimson text-sm">{orderError}</p>
                </div>
              )}

              {grandTotal <= 0 && (
                <button type="submit" disabled={orderLoading || cart.length === 0}
                  className="w-full py-4 bg-gold text-surface-primary font-bold text-lg rounded-xl shadow-gold hover:shadow-gold-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:hover:translate-y-0">
                  {orderLoading ? 'Wird gesendet...' : 'Jetzt kostenlos laden'}
                </button>
              )}
            </form>

            {grandTotal > 0 && cart.length > 0 && (
              <div className="mt-6 p-4 bg-surface rounded-2xl border border-white/[0.06]">
                <p className="text-sm text-text-secondary mb-4 text-center">
                  Sichere Zahlung via PayPal
                </p>
                {PAYPAL_CLIENT_ID ? (
                  <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: 'EUR', intent: 'capture' }}>
                    <PayPalButtons
                      disabled={orderLoading}
                      style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' }}
                      createOrder={handlePayPalCreateOrder}
                      onApprove={handlePayPalApprove}
                      onError={(err: any) => {
                        console.error('[PayPal] onError:', err);
                        setOrderError('PayPal Fehler: ' + (err.message || 'Unbekannter Fehler'));
                      }}
                      onCancel={() => setOrderLoading(false)}
                    />
                  </PayPalScriptProvider>
                ) : (
                  <div className="p-4 bg-crimson-dim border border-crimson/20 rounded-xl text-center">
                    <p className="text-crimson text-sm font-semibold">PayPal Client ID fehlt!</p>
                    <p className="text-text-muted text-xs mt-1">VITE_PAYPAL_CLIENT_ID ist nicht gesetzt.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <div className="bg-surface rounded-2xl border border-white/[0.06] p-6 sticky top-24">
              <h2 className="text-lg font-bold text-text-primary mb-5">Zusammenfassung</h2>
              <div className="space-y-4 mb-6">
                {cart.map(item => (
                  <div key={item.product.id} className="flex gap-3 pb-4 border-b border-white/[0.06]">
                    <img src={item.product.image} alt={item.product.name} className="w-16 h-16 object-cover rounded-lg flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-text-primary truncate">{item.product.name}</h4>
                      <p className="text-xs text-text-muted">{getPriceStr(item.product.price)} × {item.quantity}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <button onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                          className="w-6 h-6 flex items-center justify-center rounded bg-white/[0.05] text-text-muted hover:bg-white/[0.1]"><Minus size={12} /></button>
                        <span className="text-xs font-medium w-4 text-center">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                          className="w-6 h-6 flex items-center justify-center rounded bg-white/[0.05] text-text-muted hover:bg-white/[0.1]"><Plus size={12} /></button>
                        <button onClick={() => removeItem(item.product.id)} className="ml-auto text-text-muted hover:text-crimson transition-colors"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mb-6 p-4 bg-surface-secondary rounded-xl border border-white/[0.06]">
                <label className="block text-sm font-medium text-text-secondary mb-2 flex items-center gap-1.5">
                  <Tag size={14} />Gutscheincode
                </label>
                <div className="flex gap-2">
                  <input type="text" value={voucherCode}
                    onChange={e => setVoucherCode(e.target.value.toUpperCase())}
                    placeholder="CODE" disabled={!!voucher}
                    className="flex-1 px-3 py-2 bg-white/[0.03] border border-white/[0.06] rounded-lg text-text-primary uppercase text-sm placeholder:text-text-muted focus:border-gold focus:outline-none disabled:opacity-50" />
                  <button type="button" disabled={!voucherCode || !!voucher}
                    onClick={async () => { const r = await applyVoucher(voucherCode); if (!r.success) alert('Ungültiger Gutscheincode'); }}
                    className="px-3 py-2 bg-gold text-surface-primary text-sm font-semibold rounded-lg disabled:opacity-50">Einlösen</button>
                </div>
                {voucher && (
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-green-400">
                      {voucher.type === 'percent' ? `${voucher.code}: ${voucher.amount}%` : `${voucher.code}: ${voucher.amount.toFixed(2)} €`}
                    </span>
                    <button onClick={removeVoucher} className="text-crimson hover:underline text-xs">Entfernen</button>
                  </div>
                )}
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-text-secondary">
                  <span>Zwischensumme</span>
                  <span>{rawTotal.toFixed(2).replace('.', ',')} €</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-green-400">
                    <span>{voucher?.type === 'percent' ? `Gutschein ${voucher.amount}%` : 'Gutschein'}</span>
                    <span>-{discount.toFixed(2).replace('.', ',')} €</span>
                  </div>
                )}
                {needsShipping && (
                  <div className="flex justify-between text-text-secondary">
                    <span>Versand</span>
                    <span>{shippingCost === 0 ? 'Kostenlos' : `${shippingCost.toFixed(2).replace('.', ',')} €`}</span>
                  </div>
                )}
                {needsShipping && shippingCost === 0 && rawTotal > 0 && (
                  <p className="text-xs text-gold">Gratisversand ab 50 € ✓</p>
                )}
                <div className="flex justify-between text-lg font-bold text-gold pt-3 border-t border-white/[0.06]">
                  <span>Gesamt</span>
                  <span>{grandTotal.toFixed(2).replace('.', ',')} €</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}